/**
 * @fileoverview Riddle - Componente de la Experiencia Móvil del Aficionado para Adivina Adivinador (HU-05).
 * 
 * Funcionalidades clave:
 * - Acceso exclusivo y responsivo desde la raíz '/' (Fan Dashboard).
 * - Renderizado limpio: exclusivamente tiles con texto (sin imágenes en preguntas, opciones ni revelación).
 * - Votación táctil con respuesta inmediata.
 * - Confirmación fija ("Tú elegiste: [texto]") que se mantiene sin cambios durante toda la ronda.
 * - Persistencia de selección en sessionStorage ('eudnrlanrbaewq') ante recargas accidentales.
 * 
 * @module components/Dashboard/Sections/Riddle
 */

import React, { useEffect, useState, useMemo } from 'react';
import { SubmitRiddleResponse as BLSubmitRiddleResponse } from './../../../utilities/BL/Zones';

/**
 * Clave para almacenar el identificador único del aficionado en el dispositivo.
 * @constant {string}
 */
const STORAGE_FAN_UID_KEY = 'adsl_fan_uid';

/**
 * Letras para etiquetar las opciones en la interfaz móvil.
 * @constant {Array<string>}
 */
const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Obtiene o inicializa un UID único y persistente para el aficionado en esta sesión.
 * 
 * @returns {string} Identificador único del aficionado.
 */
const getOrCreateFanUid = () => {
    try {
        let uid = sessionStorage.getItem(STORAGE_FAN_UID_KEY);
        if (!uid) {
            uid = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
                ? crypto.randomUUID()
                : `fan_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            sessionStorage.setItem(STORAGE_FAN_UID_KEY, uid);
        }
        return uid;
    } catch (e) {
        return `fan_${Date.now()}`;
    }
};

/**
 * Componente principal de interacción para el aficionado móvil en Adivina Adivinador.
 * 
 * @function Riddle
 * @param {Object} props
 * @param {Object|null} props.riddle - Datos de la adivinanza activa emitida en tiempo real.
 * @returns {React.ReactElement}
 */
const Riddle = ({ riddle }) => {
    const [selectedOption, setSelectedOption] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Identificador normalizado de la pregunta activa
    const questionId = useMemo(() => {
        if (!riddle) return null;
        return riddle.questionId !== undefined ? riddle.questionId : (riddle.id !== undefined ? riddle.id : null);
    }, [riddle]);

    const options = useMemo(() => (Array.isArray(riddle?.options) ? riddle.options : []), [riddle?.options]);
    const instruction = riddle?.instruction || '¿Quién o qué se esconde en la sombra?';

    /**
     * Efecto de sincronización con el ciclo de vida de la adivinanza:
     * - Al cambiar de pregunta o reiniciar, el estado inicia limpio (selectedOption = null).
     * - Si la pregunta está activa y ya se había votado en esta misma ronda, se restaura desde sessionStorage.
     */
    useEffect(() => {
        setSelectedOption(null);

        if (!questionId) return;

        const tmpData = sessionStorage.getItem('eudnrlanrbaewq');
        if (tmpData) {
            try {
                const data = JSON.parse(tmpData);
                if (data && data.questionId === questionId) {
                    const matchedOption = options.find(opt => opt.id === data.id || opt.text === data.text);
                    setSelectedOption(matchedOption || data);
                }
            } catch (e) {
                console.warn('[Riddle Fan] Error al parsear voto guardado:', e);
            }
        }
    }, [riddle, questionId, options]);

    /**
     * Manejador de selección táctil ultrarrápida de una opción.
     * 
     * @async
     * @param {Object} option - Opción seleccionada por el aficionado.
     */
    const handleOptionClick = async (option) => {
        if (selectedOption !== null || isSubmitting || !questionId) {
            return;
        }

        setIsSubmitting(true);

        // 1. Confirmación visual inmediata en el cliente
        setSelectedOption(option);
        try {
            sessionStorage.setItem('eudnrlanrbaewq', JSON.stringify({
                id: option.id,
                text: option.text,
                questionId: questionId
            }));
        } catch (e) {
            console.warn('[Riddle Fan] Error al guardar voto en sessionStorage:', e);
        }

        // 2. Votación asíncrona hacia el backend SOLO si la opción seleccionada es la correcta
        if (option.isCorrect) {
            try {
                const fanUid = getOrCreateFanUid();
                const response = await BLSubmitRiddleResponse(questionId, option.id, fanUid);

                if (!response || !response.success) {
                    console.warn('[Riddle Fan] ⚠️ Aviso en registro de voto:', response?.message);
                }
            } catch (error) {
                console.error('[Riddle Fan] ❌ Error de red al registrar voto:', error);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setIsSubmitting(false);
        }
    };

    // =========================================================================
    // 1. ESTADO DE ESPERA (Sin pregunta activa o sin opciones)
    // =========================================================================
    if (!riddle || !questionId || options.length === 0) {
        return (
            <div className="RiddleFan RiddleFan--Waiting">
                <div className="RiddleFan__Card RiddleFan__Card--Standby">
                    <img src='/img/page/adsl-five.png' alt="ADSL" style={{ maxWidth: '300px', width: '80%' }} />
                    <h2 className="RiddleFan__Title mt-3">¡Prepárate en tu lugar!</h2>
                    <p className="RiddleFan__Subtitle">
                        En breve el operador proyectará una nueva adivinanza en la pantalla gigante del estadio.
                    </p>
                    <div className="RiddleFan__LiveIndicator">
                        <span className="RiddleFan__LiveDot"></span>
                        <span>Esperando adivinanza en vivo</span>
                    </div>
                </div>
            </div>
        );
    }

    // =========================================================================
    // 2. ESTADO DE CONFIRMACIÓN FIJA (Ya votó / selección elegida)
    // =========================================================================
    if (selectedOption !== null) {
        return (
            <div className="RiddleFan RiddleFan--Confirmed">
                <div className="RiddleFan__Card RiddleFan__Card--Confirmed">
                    <div className="RiddleFan__StatusHeader">
                        <img src='/img/page/adsl-five.png' alt="ADSL" style={{ maxWidth: '350px', width: '90%' }} />
                        <h2 className="RiddleFan__Title mt-3">Tú elegiste:</h2>
                    </div>

                    {/* Tarjeta de solo texto con la opción seleccionada */}
                    <div className="RiddleFan__ChosenOptionCard">
                        <div className="RiddleFan__ChosenOptionCard__Body">
                            <span className="RiddleFan__ChosenOptionCard__Text">
                                {selectedOption.text}
                            </span>
                        </div>
                        <div className="RiddleFan__ChosenOptionCard__Check">
                            <i className="bi bi-check-lg"></i>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // =========================================================================
    // 3. ESTADO DE VOTACIÓN ACTIVA (Tiles de solo texto)
    // =========================================================================
    return (
        <div className="RiddleFan RiddleFan--Active">
            <div className="RiddleFan__Card RiddleFan__Card--Voting">
                <header className="RiddleFan__Header">
                    <img src='/img/page/adsl-five.png' alt="ADSL" style={{ maxWidth: '280px', width: '75%', marginBottom: '8px' }} />
                    <span className="RiddleFan__Badge RiddleFan__Badge--Live d-block mx-auto mb-2" style={{ width: 'fit-content' }}>
                        <span className="RiddleFan__LiveDot me-1"></span> EN VIVO
                    </span>
                    <h2 className="RiddleFan__Instruction">
                        {instruction}
                    </h2>
                </header>

                <p className="RiddleFan__Prompt">
                    Selecciona una opción:
                </p>

                {/* Lista de tiles táctiles de solo texto */}
                <div className="RiddleFan__OptionsList">
                    {options.map((option, index) => {
                        const letter = OPTION_LETTERS[index % OPTION_LETTERS.length];

                        return (
                            <button
                                key={option.id || index}
                                type="button"
                                className="RiddleFan__OptionBtn"
                                style={{ touchAction: 'manipulation' }}
                                disabled={isSubmitting}
                                onClick={() => handleOptionClick(option)}
                            >
                                <span className="RiddleFan__OptionBtn__Letter">
                                    {letter}
                                </span>

                                <span className="RiddleFan__OptionBtn__Text">
                                    {option.text}
                                </span>

                                <span className="RiddleFan__OptionBtn__Chevron">
                                    <i className="bi bi-chevron-right"></i>
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export { Riddle };